import React from 'react';
import { Link } from 'react-router-dom';
import PlanAwareLink from '../billing/PlanAwareLink';
import PlanTierLabel from '../billing/PlanTierLabel';
import { FEATURE } from '../../config/planFeatures';
import { useUserPlan } from '../../hooks/useUserPlan';
import { LuFlame, LuGauge, LuLayoutDashboard, LuSparkles, LuTrophy } from 'react-icons/lu';
import { STUDY_HUB } from '../../config/appLabels';
import { GAMIFICATION_NAV } from '../../config/gamificationNav';
import useGamificationSummary from '../../hooks/useGamificationSummary';
import '../learning/learning-page-ui.css';
import './GamificationQuickBar.css';

function StatChip({ icon: Icon, label, value, accent }) {
  return (
    <div className={`gamification-stat-chip${accent ? ' gamification-stat-chip--accent' : ''}`}>
      <span className="gamification-stat-chip-icon" aria-hidden>
        <Icon />
      </span>
      <span className="gamification-stat-chip-text">
        <span className="gamification-stat-chip-label">{label}</span>
        <span className="gamification-stat-chip-value">{value}</span>
      </span>
    </div>
  );
}

export default function GamificationQuickBar() {
  const { summary, loading, error } = useGamificationSummary();
  const { canUseFeature } = useUserPlan();
  const leaderboardLocked = !canUseFeature(FEATURE.LEADERBOARD_RANK);

  const level = summary?.level ?? 1;
  const score = summary?.learningScore ?? 0;
  const xp = summary?.lifetimeXp ?? 0;
  const streak = summary?.currentStreak ?? 0;
  const levelPct = summary?.levelProgressPercent ?? 0;

  return (
    <aside className="gamification-quick-bar" aria-label="Learning progress">
      <div className="gamification-quick-bar-metrics">
        <StatChip icon={LuSparkles} label="Level" value={level} accent />
        <StatChip icon={LuGauge} label="Score" value={`${score}/100`} />
        <StatChip icon={LuTrophy} label="Lifetime XP" value={xp.toLocaleString()} />
        <StatChip
          icon={LuFlame}
          label="Streak"
          value={streak > 0 ? `${streak} day${streak === 1 ? '' : 's'}` : '—'}
          accent={streak > 0}
        />
      </div>
      {levelPct > 0 && levelPct < 100 && (
        <div
          className="gamification-quick-bar-level"
          role="progressbar"
          aria-valuenow={levelPct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Progress to next level"
        >
          <div className="gamification-quick-bar-level-fill" style={{ width: `${levelPct}%` }} />
        </div>
      )}
      <nav className="gamification-quick-bar-nav" aria-label="Progress shortcuts">
        <Link to={GAMIFICATION_NAV.profile.path} className="qd-btn qd-btn--secondary gamification-quick-bar-link">
          <LuGauge aria-hidden />
          {GAMIFICATION_NAV.profile.shortLabel}
        </Link>
        <PlanAwareLink
          to={GAMIFICATION_NAV.leaderboard.path}
          className="qd-btn qd-btn--secondary gamification-quick-bar-link"
        >
          <LuTrophy aria-hidden />
          {GAMIFICATION_NAV.leaderboard.shortLabel}
          {leaderboardLocked && <PlanTierLabel tier="Pro" />}
        </PlanAwareLink>
        <Link to={STUDY_HUB.path} className="qd-btn qd-btn--secondary gamification-quick-bar-link">
          <LuLayoutDashboard aria-hidden />
          {STUDY_HUB.shortNavLabel || 'Home'}
        </Link>
      </nav>
      {loading && <p className="gamification-quick-bar-hint">Syncing progress…</p>}
      {error && (
        <p className="gamification-quick-bar-warn" role="status">
          Progress sync unavailable — update the API or open Study hub after restart.
        </p>
      )}
      {!loading && !error && xp === 0 && (
        <p className="gamification-quick-bar-hint">Earn XP by reading, practicing, and completing reviews.</p>
      )}
    </aside>
  );
}
